'use client';

import { Box, Stack, Typography } from '@mui/material';
import { IngestionSourceType } from '@repo/api-gw-sdk';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';

import type { IngestionPipelineWizardState } from '../IngestionPipelineWizardState';
import { PathBuilder } from '../partitioning/PathBuilder';

const SectionHeader = ({ title }: { title: string }) => (
  <Typography
    variant='subtitle1'
    sx={{
      fontWeight: 600,
      borderBottom: '1px solid',
      borderColor: 'divider',
      pb: 1,
      mb: 1,
    }}
  >
    {title}
  </Typography>
);

const Field = ({ label, value }: { label: string; value: string }) => (
  <Stack direction='row' gap={2}>
    <Typography
      variant='body2'
      sx={{
        color: 'text.secondary',
        minWidth: 160,
      }}
    >
      {label}
    </Typography>
    <Typography variant='body2'>{value}</Typography>
  </Stack>
);

export function ReviewStep(props: StepProps<IngestionPipelineWizardState>) {
  const getSourceTypeDisplayText = () => {
    if (
      props.wizardState.sourceType ===
      IngestionSourceType.INGESTION_SOURCE_TYPE_S3
    ) {
      return 'S3';
    }
    if (
      props.wizardState.sourceType ===
      IngestionSourceType.INGESTION_SOURCE_TYPE_GCS
    ) {
      return 'GCS';
    }
    return String(props.wizardState.sourceType);
  };

  return (
    <StepContainer
      stepperIndex={3}
      stepperLabels={props.stepperLabels}
      canGoNext={() => true}
      onNextClick={async () => {
        await props.finish(
          props.wizardState.isUpdateMode
            ? 'Ingestion pipeline updated successfully'
            : 'Ingestion pipeline created successfully'
        );
      }}
      onBackClick={() => props.back()}
      nextButtonText={props.wizardState.isUpdateMode ? 'Update' : 'Create'}
    >
      <Stack
        sx={{ height: '100%' }}
        alignItems='center'
        justifyContent='center'
      >
        <Stack width='600px' gap={3}>
          {/* Pipeline Details Section */}
          <Box>
            <SectionHeader title='Pipeline Details' />
            <Stack gap={2}>
              <Field label='Pipeline Name' value={props.wizardState.name} />
              <Field
                label='Table Namespace'
                value={props.wizardState.namespace}
              />
              <Field label='Table Name' value={props.wizardState.tableName} />
            </Stack>
          </Box>

          {/* Source Section */}
          <Box>
            <SectionHeader title='Source' />
            <Stack gap={2}>
              <Field label='Source Type' value={getSourceTypeDisplayText()} />
              {props.wizardState.sourceType ===
              IngestionSourceType.INGESTION_SOURCE_TYPE_S3 ? (
                <>
                  <Field label='Bucket' value={props.wizardState.s3Bucket} />
                  <Field label='Region' value={props.wizardState.s3Region} />
                  <Field
                    label='Owner Account ID'
                    value={props.wizardState.s3OwnerAccountId}
                  />
                </>
              ) : (
                <>
                  <Field label='Bucket' value={props.wizardState.gcsBucket} />
                  <Field
                    label='Location'
                    value={props.wizardState.gcsLocation}
                  />
                  <Field
                    label='Project ID'
                    value={props.wizardState.gcsProjectId}
                  />
                </>
              )}
              <Field label='File Format' value={props.wizardState.fileFormat} />
            </Stack>
          </Box>

          {/* Partitioning Section */}
          <Box>
            <SectionHeader title='Partitioning' />
            <Stack gap={2}>
              <Field
                label='Separator'
                value={props.wizardState.partitionSpec.separator}
              />
              <PathBuilder
                segments={props.wizardState.partitionSpec.segments}
                separator={props.wizardState.partitionSpec.separator}
                readOnly
              />
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </StepContainer>
  );
}
